import Config from './config'
import Phaser from 'phaser'
import Artifact from '../artifact/artifact'
import Planet from '../planet/planet'
import Star from '../star/star'
import Ship from '../ship/ship'
import Satellite from '../satellite/satellite'
import dev1 from './dev-1'
import dev2 from './dev-2'
import devEntities from './dev-entities'
import devArtifacts from './dev-artifacts'
import devSelectBox from './dev-select-box'
import devSleep from './dev-sleep'
import devSound from './dev-sound'
import {isDebug} from './utils'

import {getStoredState} from './store'
// import {mockData0} from './mock-data'
// import {addStoredGameStats} from './store'

const createEntityPreview = (entity) =>
    new Promise((resolve, reject) => {
        const width = 100
        const height = width

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = width
        canvas.id = `entity-preview-${entity.id}`

        const ctx = canvas.getContext('2d')

        document
            .querySelector(`#${Config.entityPreviewHolderId}`)
            .appendChild(canvas)

        if (entity.type === 'star') {
            const img = new Image()
            img.src = 'img/star-entity.png'
            img.addEventListener('load', () => {
                const imgCanvas = document.createElement('canvas')
                const imgCtx = imgCanvas.getContext('2d')
                imgCanvas.width = width
                imgCanvas.height = height
                imgCtx.drawImage(img, 0, 0, width, height)
                imgCtx.globalCompositeOperation = 'source-atop'
                imgCtx.fillStyle = entity.fillColor.replace('0x', '#')
                imgCtx.fillRect(0, 0, width, height)
                imgCtx.globalCompositeOperation = 'source-over'

                // Draw the grayscale image onto the canvas
                ctx.drawImage(img, 0, 0, width, height)

                // Set compositing
                ctx.globalCompositeOperation = 'multiply'

                // Draw the fully green img on top of the grayscale image
                ctx.drawImage(imgCanvas, 0, 0, width, height)

                // Change compositing back to default to clean up
                ctx.globalCompositeOperation = 'source-over'
            })

            return resolve()
        }

        // Radial gradient arc
        const radius = 34
        const startX = 50
        const startY = 50
        const innerRadius = 2
        const outerRadius = 90
        const innerColor = entity.fillColorLight.replace('0x', '#')
        const middleColor = entity.fillColor.replace('0x', '')
        const outerColor = entity.fillColorDark.replace('0x', '#')
        const radialGradient = ctx.createRadialGradient(
            startX,
            startY,
            innerRadius,
            startX - 20,
            startY - 20,
            outerRadius
        )
        radialGradient.addColorStop(0, innerColor)
        middleColor && radialGradient.addColorStop(0.5, `#${middleColor}`)
        radialGradient.addColorStop(0.9, outerColor)

        ctx.fillStyle = radialGradient
        ctx.arc(startX, startY, radius, 0, 2 * Math.PI)
        ctx.fill()

        let imgX = 8
        let imgY = imgX
        const imgDiameter = radius * 2

        // Planet Feature
        const loadFeatureImage = () =>
            new Promise((resolve) => {
                const featureImg = new Image()
                featureImg.src = `img/planet-feature${entity.featureId}.png`
                featureImg.addEventListener(
                    'load',
                    () => {
                        const imgCanvas = document.createElement('canvas')
                        imgCanvas.width = width
                        imgCanvas.height = width

                        const imgCtx = imgCanvas.getContext('2d')
                        imgCtx.drawImage(
                            featureImg,
                            imgX,
                            imgY,
                            imgDiameter,
                            imgDiameter
                        )
                        imgCtx.globalCompositeOperation = 'source-atop'
                        imgCtx.fillStyle = entity.featureColor.replace(
                            '0x',
                            '#'
                        )
                        imgCtx.fillRect(imgX, imgY, imgDiameter, imgDiameter)
                        ctx.drawImage(imgCanvas, imgX, imgY, width, height)
                        ctx.globalCompositeOperation = 'source-over'

                        return resolve()
                    },
                    false
                )
            })

        // Gloss overlay
        const loadOverlayImg = () =>
            new Promise((resolve) => {
                imgX = imgX * 2
                imgY = imgX

                const glossImg = new Image()
                glossImg.src = 'img/planet-overlay.png'
                glossImg.addEventListener('load', () => {
                    ctx.drawImage(
                        glossImg,
                        imgX,
                        imgY,
                        imgDiameter,
                        imgDiameter
                    )
                    return resolve()
                })
            })

        return loadFeatureImage()
            .then(() => loadOverlayImg())
            .then(() => resolve())
    })

export const generateEntityPreviews = (entities) =>
    new Promise((resolve) => {
        document.querySelector(`#${Config.entityPreviewHolderId}`).innerHTML =
            ''

        const promises = []

        Object.keys(entities).forEach((key) => {
            promises.push(createEntityPreview(entities[key]))
        })

        return Promise.all(promises).then(() => resolve())
    })

const buildSceneFromStore = (state) => {
    let startingPlanet = Object.keys(state.entities).find(
        ({isHomePlanet}) => isHomePlanet
    )

    Object.assign(Config, state.config)
    Config.gameStats = state.gameStats

    // Artifacts
    Object.keys(state.artifacts).forEach((key) => {
        new Artifact({
            ...state.artifacts[key],
            id: key,
        })
    })

    // Entities
    Object.keys(state.entities).forEach((key) => {
        const entity = state.entities[key]
        const orbitingTowables = {}

        Object.keys(state.artifacts).forEach((artifactKey) => {
            const artifact = state.artifacts[artifactKey]
            if (artifact.orbitingEntityId === key) {
                orbitingTowables[artifactKey] = {id: artifactKey}
            }
        })

        entity.orbitingTowables = orbitingTowables

        if (entity.type === 'planet') {
            const planet = new Planet({...entity, id: key})

            // Find a friendly planet to center camera on if home isn't available
            if (!startingPlanet && planet.faction === Config.userFactionName) {
                startingPlanet = planet
            }
        }

        if (entity.type === 'star') {
            new Star({...entity})
        }
    })

    // Ships
    Object.keys(state.ships).forEach((key) => {
        const ship = state.ships[key]
        let orbitingEntity

        Object.keys(Config.entities).forEach((entityKey) => {
            const {orbitingShips} = Config.entities[entityKey]

            Object.keys(orbitingShips).forEach((shipKey) => {
                if (key === shipKey) {
                    orbitingEntity = Config.entities[entityKey]
                }
            })
        })

        if (ship.type === 'satellite') {
            new Satellite({
                ...ship,
                orbitingEntity,
            })
            return
        }

        new Ship({
            ...ship,
            orbitingEntity,
        })
    })

    return {
        planet: startingPlanet,
    }
}

const getStartZones = () => {
    const boardSize = Config.boardSizes[Config.boardSize]
    const padding = Config.boardPadding
    const width = boardSize.boardWidth / 6
    const height = boardSize.boardHeight / 6

    // Create a starting zone in each corner of the board
    const zones = [
        {
            x: padding,
            y: padding,
        },
        {
            x: boardSize.boardWidth - width - padding,
            y: padding,
        },
        {
            x: padding,
            y: boardSize.boardHeight - height - padding,
        },
        {
            x: boardSize.boardWidth - width - padding,
            y: boardSize.boardHeight - height - padding,
        },
    ]

    // Draw zones
    if (isDebug) {
        zones.forEach(({x, y}, index) => {
            const rect = new Phaser.Geom.Rectangle(x, y, width, height)
            const graphics = Config.scene.add.graphics({
                lineStyle: {width: 2, color: 0xffffff},
            })
            const text = Config.scene.add.text(
                x + 5,
                y + 5,
                `${index} - x:${x} y:${y}`,
                {
                    fontFamily: 'Roboto, sans-serif',
                    fontSize: '17px',
                    color: '#ffffff',
                }
            )

            graphics.strokeRectShape(rect)
            graphics[Config.namespace] = {type: 'zone'}

            zones[index].group = Config.scene.add.group({
                classType: Phaser.GameObjects.Sprite,
            })
            zones[index].group.addMultiple([graphics, text])
        })
    }

    // Ensure that the enemy starting zone is not the user starting zone
    const userStartZoneIndex = Phaser.Math.Between(0, zones.length - 1)
    const userStartZone = zones[userStartZoneIndex]
    userStartZone.isUser = true
    const enemyStartZone = zones.find(({isUser}) => !isUser)

    // Randomly place planet inside zone
    let data = {
        [Config.userFactionName]: {
            x: Phaser.Math.Between(userStartZone.x, userStartZone.x + width),
            y: Phaser.Math.Between(userStartZone.y, userStartZone.y + height),
        },
        [Config.enemyFactionName]: {
            x: Phaser.Math.Between(enemyStartZone.x, enemyStartZone.x + width),
            y: Phaser.Math.Between(enemyStartZone.y, enemyStartZone.y + height),
        },
    }

    if (Config.enemyFactions.length) {
        delete data[Config.enemyFactionName]

        if (Config.isDemo) {
            data = {}
        }

        Config.enemyFactions.forEach((faction, index) => {
            // const zone = zones.filter(({isUser}) => !isUser)
            const zone = zones[index]
            data[faction] = {
                x: Phaser.Math.Between(zone.x, zone.x + width),
                y: Phaser.Math.Between(zone.y, zone.y + height),
            }
        })
    }

    return data
}

const getEntityPositions = ({nonPlanetCount, startZones}) =>
    new Promise((resolve, reject) => {
        const placeholders = {}

        class Placeholder extends Phaser.GameObjects.Graphics {
            constructor(scene, options) {
                super(scene, options)

                const {faction, id, startCoords} = options

                const x = startCoords ? startCoords.x : this.randomCoords().x
                const y = startCoords ? startCoords.y : this.randomCoords().y

                this.id = id
                placeholders[id] = this
                this.faction = faction || false

                this.circle = new Phaser.Geom.Circle(
                    x,
                    y,
                    Config.entityPlaceholderRadius
                )
                if (isDebug) {
                    this.fillStyle(this.faction ? '0xFF0000' : '0xffffff')
                    this.fillCircleShape(this.circle)
                    this.setDepth(1)
                    this[Config.namespace] = {type: 'placeholder'}

                    this.text = Config.scene.add.text(
                        x,
                        y,
                        `${this.id} x${x} y${y}`,
                        {
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '12px',
                            color: this.faction ? '#FFFFFF' : '#FF0000',
                        }
                    )
                    this.text.setDepth(2)
                    this.text[Config.namespace] = {type: 'placeholder text'}

                    scene.add.existing(this.text)
                }
                scene.add.existing(this)
            }

            reposition() {
                const x = this.randomCoords().x
                const y = this.randomCoords().y

                this.circle.x = x
                this.circle.y = y
                this.clear()
                if (isDebug) {
                    this.fillStyle(this.faction ? '0xFF0000' : '0xffffff')
                    this.fillCircleShape(this.circle)

                    this.text.setText(`${this.id} x${x} y${y}`)
                    this.text.x = this.circle.x
                    this.text.y = this.circle.y
                }
            }

            randomCoords() {
                return {
                    x: Phaser.Math.Between(
                        Config.boardPadding,
                        Config.boardSizes[Config.boardSize].boardWidth -
                            Config.boardPadding
                    ),
                    y: Phaser.Math.Between(
                        Config.boardPadding,
                        Config.boardSizes[Config.boardSize].boardHeight -
                            Config.boardPadding
                    ),
                }
            }
        }

        let placeholderCollisions = {}
        const placeholderCount =
            Config.boardSizes[Config.boardSize].planetCount + nonPlanetCount

        const startZoneCount = Object.keys(startZones).length
        if (Config.isDemo) {
            Object.keys(startZones).forEach((key, index) => {
                new Placeholder(Config.scene, {
                    faction: key,
                    id: index,
                    startCoords: startZones[key],
                })
            })
        } else {
            new Placeholder(Config.scene, {
                faction: Config.userFactionName,
                id: 0,
                startCoords: startZones[Config.userFactionName],
            })
            new Placeholder(Config.scene, {
                faction: Config.enemyFactionName,
                id: 1,
                startCoords: startZones[Config.enemyFactionName],
            })
        }

        for (let i = startZoneCount; i < placeholderCount; i += 1) {
            new Placeholder(Config.scene, {id: i})
        }

        const checkForCollisions = () => {
            Object.keys(placeholders).forEach((aKey) => {
                const a = placeholders[aKey]

                Object.keys(placeholders).forEach((bKey) => {
                    const b = placeholders[bKey]

                    if (a.id === b.id) {
                        return
                    }

                    const collision = Phaser.Geom.Intersects.CircleToCircle(
                        a.circle,
                        b.circle
                    )

                    if (collision && !a.faction) {
                        a.reposition()
                        placeholderCollisions[a.id] = true
                    }
                })
            })
        }

        let placeholderAttempts = Config.entityPlaceholderCollisonAttempts

        const placeholderCollisionTimer = window.setInterval(() => {
            checkForCollisions()
            isDebug &&
                console.log(
                    'placeholder collisions attempts',
                    Object.keys(placeholderCollisions).length,
                    placeholderAttempts
                )

            if (
                !placeholderAttempts ||
                !Object.keys(placeholderCollisions).length
            ) {
                clearInterval(placeholderCollisionTimer)

                const entityPositions = Object.keys(placeholders).map((key) => {
                    const {circle, faction, id} = placeholders[key]

                    return {
                        faction,
                        id,
                        x: circle.x,
                        y: circle.y,
                    }
                })

                Object.keys(placeholders).forEach((key) => {
                    placeholders[key].text && placeholders[key].text.destroy()
                    placeholders[key].destroy()
                    delete placeholders[key]
                })

                return resolve(entityPositions)
            }

            placeholderAttempts -= 1
            placeholderCollisions = {}
        }, 0)
    })

const defaultScene = () =>
    new Promise((resolve, reject) => {
        const stored = getStoredState()
        const difficulty = Config.difficultyLevels[Config.difficulty]
        const {artifactCount, starCountMax, starCountMin} = Config.boardSizes[
            Config.boardSize
        ]
        Config.gameStats.resources = difficulty.resources

        if (stored) {
            if (stored.careerStats) {
                Config.careerStats = stored.careerStats
            }

            if (!stored.isResetting) {
                return resolve(buildSceneFromStore(stored))
            }
        }

        const starCount = Config.chance.integer({
            min: starCountMin,
            max: starCountMax,
        })

        const nonPlanetCount = starCount + artifactCount
        const startZones = getStartZones()

        return getEntityPositions({nonPlanetCount, startZones}).then(
            (entityPositions) => {
                let manualInsertIndex = 0

                // Use the first entity position for user home planet
                const homePlanet = new Planet({
                    faction: Config.userFactionName,
                    colonized: true,
                    discovered: true,
                    isHomePlanet: true,
                    isMiningResources: true,
                    x: entityPositions[0].x,
                    y: entityPositions[0].y,
                })
                entityPositions[0].assigned = true

                // Create a friendly ship orbiting home planet
                for (let i = 0; i < difficulty.userShipCount; i += 1) {
                    new Ship({
                        faction: Config.userFactionName,
                        orbitingEntity: homePlanet,
                        x: homePlanet.x,
                        y: homePlanet.y,
                    })
                }

                // Use the second entity position for enemy home planet
                const enemyPlanet = new Planet({
                    faction: Config.enemyFactionName,
                    colonized: true,
                    isEnemyHomePlanet: true,
                    x: entityPositions[1].x,
                    y: entityPositions[1].y,
                })
                entityPositions[1].assigned = true

                // Create an enemy ship orbiting enemy planet
                for (let i = 0; i < difficulty.enemyShipCount; i += 1) {
                    new Ship({
                        faction: Config.enemyFactionName,
                        orbitingEntity: enemyPlanet,
                        x: enemyPlanet.x,
                        y: enemyPlanet.y,
                    })
                }

                manualInsertIndex = 2

                // Create stars from star count
                // for (
                //     let i = manualInsertIndex;
                //     i < starCount + manualInsertIndex;
                //     i += 1
                // ) {
                //     new Star({
                //         x: entityPositions[i].x,
                //         y: entityPositions[i].y,
                //     })
                //     entityPositions[i].assigned = true
                // }
                //
                // manualInsertIndex += starCount

                // Create artifacts from artifact count
                for (
                    let i = manualInsertIndex;
                    i < artifactCount + manualInsertIndex;
                    i += 1
                ) {
                    new Artifact({
                        x: entityPositions[i].x,
                        y: entityPositions[i].y,
                    })
                    entityPositions[i].assigned = true
                }

                manualInsertIndex += artifactCount

                // The rest of the entity positions will be planets
                entityPositions.forEach(({assigned}, index) => {
                    if (assigned) {
                        return
                    }

                    new Planet({
                        x: entityPositions[index].x,
                        y: entityPositions[index].y,
                    })
                    entityPositions[index].assigned = true
                })

                return resolve({planet: homePlanet})
            }
        )
    })

const demoScene = () =>
    new Promise((resolve, reject) => {
        const difficulty = Config.difficultyLevels[Config.difficulty]

        Config.gameStats.resources = difficulty.resources
        Config.boardSize = 'size1'
        Config.difficulty = 'difficulty3'
        Config.enemyFactions = ['opfor0', 'opfor1', 'opfor2', 'opfor3']

        const startZones = getStartZones()

        return getEntityPositions({nonPlanetCount: 0, startZones}).then(
            (entityPositions) => {
                let conflict0

                const addEnemy = (faction, index) => {
                    const enemyPlanet = new Planet({
                        faction,
                        colonized: true,
                        isEnemyHomePlanet: true,
                        x: entityPositions[index].x,
                        y: entityPositions[index].y,
                        discovered: true,
                    })

                    if (index === 0) {
                        conflict0 = enemyPlanet
                    }

                    // Create an enemy ship orbiting enemy planet
                    for (let i = 0; i < 1; i += 1) {
                        new Ship({
                            faction,
                            orbitingEntity: enemyPlanet,
                            x: enemyPlanet.x,
                            y: enemyPlanet.y,
                        })
                    }
                    new Satellite({
                        faction,
                        orbitingEntity: enemyPlanet,
                        x: enemyPlanet.x,
                        y: enemyPlanet.y,
                    })
                }

                Config.enemyFactions.forEach((faction, index) => {
                    addEnemy(faction, index)
                    entityPositions[index].assigned = true
                })

                new Ship({
                    faction: 'opfor1',
                    orbitingEntity: conflict0,
                    x: conflict0.x,
                    y: conflict0.y,
                })
                new Ship({
                    faction: 'opfor2',
                    orbitingEntity: conflict0,
                    x: conflict0.x,
                    y: conflict0.y,
                })
                new Ship({
                    faction: 'opfor3',
                    orbitingEntity: conflict0,
                    x: conflict0.x,
                    y: conflict0.y,
                })

                // The rest of the entity positions will be planets
                entityPositions.forEach(({assigned}, index) => {
                    if (assigned) {
                        return
                    }

                    new Planet({
                        x: entityPositions[index].x,
                        y: entityPositions[index].y,
                        discovered: true,
                    })
                    entityPositions[index].assigned = true
                })

                return resolve()
            }
        )
    })

export const chooseScene = ({isDemo}) =>
    new Promise((resolve) => {
        if (isDemo) {
            return resolve(demoScene())
        }

        const scenes = {
            dev1,
            dev2,
            devArtifacts,
            devEntities,
            devSelectBox,
            devSleep,
            devSound,
        }

        const key = Object.keys(scenes).find((key) =>
            new RegExp(key).test(window.location.href)
        )

        return resolve(scenes[key] ? scenes[key]() : defaultScene())
    })
